import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, CheckSquare, Square } from "lucide-react";
import {
  MODULE_GROUPS, ALL_MODULE_KEYS, ALWAYS_ALLOWED,
} from "@/lib/moduleCatalog";

/**
 * Dialog that lets an administrator toggle exactly which modules a user
 * can view. When `fullAccess` toggle is ON we save `module_access = null`
 * which means "full access" (default). Turning it OFF reveals the
 * checkbox grid — the user can only see whatever is checked.
 */
export default function ModulePermissionsDialog({ user, open, onClose, onSaved }) {
  const [fullAccess, setFullAccess] = useState(true);
  const [allowed, setAllowed] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    const ma = user.module_access;
    if (ma === null || ma === undefined) {
      setFullAccess(true);
      setAllowed(new Set(ALL_MODULE_KEYS));
    } else {
      setFullAccess(false);
      setAllowed(new Set([...ALWAYS_ALLOWED, ...ma]));
    }
  }, [open, user]);

  if (!user) return null;

  const toggle = (key) => {
    if (ALWAYS_ALLOWED.includes(key)) return;   // Dashboard is always on
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (groupModules) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      const groupKeys = groupModules.map((m) => m.key)
        .filter((k) => !ALWAYS_ALLOWED.includes(k));
      const allOn = groupKeys.every((k) => next.has(k));
      if (allOn) groupKeys.forEach((k) => next.delete(k));
      else groupKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = fullAccess
        ? { module_access: null }
        : { module_access: [...allowed].filter((k) => !ALWAYS_ALLOWED.includes(k)) };
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Module permissions saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="module-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck size={16} /> Module access · {user.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3 mb-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {fullAccess ? "Full access (default)" : "Restricted access"}
            </div>
            <div className="text-xs text-gray-600 mt-0.5">
              {fullAccess
                ? "User sees every module their role permits."
                : "User will only see the modules ticked below. Executive Dashboard is always visible."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Full access</span>
            <Switch checked={fullAccess} onCheckedChange={setFullAccess}
              data-testid="module-fullaccess-switch" />
          </div>
        </div>

        <fieldset disabled={fullAccess} className={fullAccess ? "opacity-50 pointer-events-none" : ""}>
          <div className="space-y-5">
            {MODULE_GROUPS.map((g) => {
              const groupKeys = g.modules.map((m) => m.key)
                .filter((k) => !ALWAYS_ALLOWED.includes(k));
              const allOn = groupKeys.length > 0 && groupKeys.every((k) => allowed.has(k));
              const someOn = !allOn && groupKeys.some((k) => allowed.has(k));
              return (
                <div key={g.label}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-800">
                      {g.label}
                    </div>
                    {groupKeys.length > 0 && (
                      <button type="button" onClick={() => toggleGroup(g.modules)}
                        className="text-xs text-emerald-800 hover:underline flex items-center gap-1"
                        data-testid={`module-group-toggle-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {allOn ? <CheckSquare size={12} /> : <Square size={12} />}
                        {allOn ? "Uncheck all" : someOn ? "Check remaining" : "Check all"}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {g.modules.map((m) => {
                      const locked = ALWAYS_ALLOWED.includes(m.key);
                      const checked = locked || allowed.has(m.key);
                      return (
                        <label key={m.key}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm border ${
                            checked ? "border-emerald-200 bg-emerald-50/50" : "border-gray-200 bg-white"
                          } ${locked ? "cursor-not-allowed" : "cursor-pointer hover:bg-emerald-50"}`}
                          data-testid={`module-checkbox-row-${m.key}`}>
                          <Checkbox checked={checked} disabled={locked}
                            onCheckedChange={() => toggle(m.key)}
                            data-testid={`module-checkbox-${m.key}`} />
                          <span className={`flex-1 truncate ${locked ? "text-gray-500" : "text-gray-800"}`}>
                            {m.label}
                          </span>
                          {locked && <span className="text-[9px] uppercase tracking-wider text-gray-400">Locked</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-emerald-900 hover:bg-emerald-950 text-white"
            data-testid="module-permissions-save">
            {busy ? "Saving…" : "Save permissions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
